import { useState, useEffect, useContext } from "react";
import { UserContext } from '../Context/UserContext';
import { useNavigate } from "react-router-dom"
import ColorLabels from "./ColorLabels";
import "../styles/singleGameMobileList.css"

export default function SingleGameMobileList({ relevantGame, ratingTimeframe }) {
    const { state, dispatch, isActiveSub } = useContext(UserContext);
    const navigate = useNavigate()
    const [dataTimeframe, setDataTimeframe] = useState("ThreeYear")
    const homeTeam = relevantGame?.home_team
    const [openStatId, setOpenStatID] = useState(null)

    useEffect(() => {
        setDataTimeframe(ratingTimeframe ? "CurrentSeason" : "ThreeYear")
    }, [ratingTimeframe])

    const isUserGame = !!(relevantGame && relevantGame.calculation_status !== undefined);
    const dailyKey = isUserGame ? `userGameDaily${dataTimeframe}Stats` : `daily${dataTimeframe}Stats`
    const pitcherKey = isUserGame ? `userGameDailyPitcher${dataTimeframe}Stats` : `dailyPitcher${dataTimeframe}Stats`

    const dailyStats = state[dailyKey];
    const dailyPitcherStats = state[pitcherKey];

    const userGameDailyHitterStats = dailyStats?.filter((stat) => stat.user_game_id === relevantGame.id)
    const regGameDailyHitterStats = dailyStats?.filter((stat) => stat.hitter_team === homeTeam || stat.pitcher_team === homeTeam)

    const relevantHitterStats = isUserGame ? userGameDailyHitterStats : regGameDailyHitterStats

    const userGameDailyPitcherStats = dailyPitcherStats?.filter((stat) => stat.user_game_id === relevantGame.id)
    const regGameDailyPitcherStats = dailyPitcherStats?.filter((stat) => stat.pitcher_team === homeTeam || stat.pitcher_team === relevantGame?.away_team)

    const relevantPitcherStats = isUserGame ? userGameDailyPitcherStats : regGameDailyPitcherStats

    const [relevantHitterState, setRelevantHitterState] = useState(relevantHitterStats ? relevantHitterStats.sort((a, b) => b.hit_matchup_rating - a.hit_matchup_rating) : [])
    const [relevantPitcherState, setRelevantPitcherState] = useState(relevantPitcherStats ? relevantPitcherStats : [])

    useEffect(() => {
        setRelevantHitterState(relevantHitterStats)
        setRelevantPitcherState(relevantPitcherStats)
    }, [homeTeam, ratingTimeframe, dailyStats, dailyPitcherStats])

    // ── Tier helpers ──────────────────────────────────────────────
    function getHitterTierInfo(rating) {
        if (rating >= 0.450) return { label: "Elite",     bg: "#E6F1FB", color: "#185FA5", bar: "#378ADD" }
        if (rating >= 0.400) return { label: "Strong",    bg: "#E1F5EE", color: "#0F6E56", bar: "#1D9E75" }
        if (rating >  0.333) return { label: "Favorable", bg: "#FAEEDA", color: "#854F0B", bar: "#F5C518" }
        if (rating >  0.230) return { label: "Average",   bg: "#D3D1C7", color: "#444441", bar: "#888780" }
        if (rating !== null && rating <= 0.230)
                             return { label: "Poor",      bg: "#FCEBEB", color: "#A32D2D", bar: "#E24B4A" }
        return               {        label: null,        bg: null,      color: null,      bar: "#888780" }
    }

    // Lower pitcher_matchup_rating_thirty_percent = better matchup for the pitcher
    function getPitcherTierInfo(rating) {
        if (rating == null) return { label: null, bg: null, color: null, bar: "#888780" }
        if (rating <= 5.63)                      return { label: "Elite",     bg: "#E6F1FB", color: "#185FA5", bar: "#378ADD" }
        if (rating > 5.63 && rating <= 5.85)     return { label: "Strong",    bg: "#E1F5EE", color: "#0F6E56", bar: "#1D9E75" }
        if (rating > 5.85 && rating < 6.05)      return { label: "Favorable", bg: "#FAEEDA", color: "#854F0B", bar: "#F5C518" }
        if (rating >= 6.4)                       return { label: "Poor",      bg: "#FCEBEB", color: "#A32D2D", bar: "#E24B4A" }
        return                                          { label: "Average",   bg: "#D3D1C7", color: "#444441", bar: "#888780" }
    }

    function fetchSingleHitterData(hitterID, isUserGame, relevantGameID) {
        fetch(`/hitters/${hitterID}?isUserGame=${isUserGame}&relevantGameID=${relevantGameID}`)
            .then(res => res.json())
            .then(data => {
                if (data) {
                    dispatch({ type: "fetchSingleHitter", payload: data })
                } else {
                    console.error("Error: Hitter data not found")
                }
            })
    }

    function navigateToHitterPage(hitterName, hitterID) {
        state.singleHitter = []
        const formattedName = hitterName.replace(/ /g, "-")
        fetchSingleHitterData(hitterID, isUserGame, relevantGame.id)
        navigate(`/hitter/${formattedName}`, {
            state: {
                isUserGame: isUserGame,
                relevantGameID: relevantGame.id
            }
        })
        window.scrollTo(0, 0)
    }

    function navigateToPitcherPage(pitcherName, pitcherID) {
        state.singlePitcher = []
        const formattedName = pitcherName.replace(/ /g, "-")
        fetchSinglePitcherData(pitcherID, isUserGame, relevantGame.id)
        navigate(`/pitcher/${formattedName}`, {
            state: {
                isUserGame: isUserGame,
                relevantGameID: relevantGame.id
            }
        })
        window.scrollTo(0, 0)
    }

    function fetchSinglePitcherData(pitcherID, isUserGame, relevantGameID) {
        fetch(`/pitchers/${pitcherID}?isUserGame=${isUserGame}&relevantGameID=${relevantGameID}`)
            .then(res => res.json())
            .then(data => {
                if (data) {
                    dispatch({ type: "fetchSinglePitcher", payload: data })
                } else {
                    console.error("Error: Pitcher data not found")
                }
            })
    }

    function expandHitterRow(statID) {
        setOpenStatID(prev => {
            return prev === statID ? null : statID
        })
    }

    return (
        <div>
            {isActiveSub && relevantPitcherState?.length > 0 && (
                <div className="singleGamePitcherListContainer">
                    {relevantPitcherState?.map((stat) => {
                        const isOpen = openStatId === stat.id
                        const tier = getPitcherTierInfo(stat.pitcher_matchup_rating_thirty_percent)
                        const teamStyles = { color: state.teamColors[stat.pitcher_team].primary };

                        const ratingBg =
                            stat.pitcher_matchup_rating_thirty_percent <= 5.63 ? "lightblue" :
                            stat.pitcher_matchup_rating_thirty_percent > 5.63 && stat.pitcher_matchup_rating_thirty_percent <= 5.85 ? "lightgreen" :
                            stat.pitcher_matchup_rating_thirty_percent > 5.85 && stat.pitcher_matchup_rating_thirty_percent < 6.05 ? "lightyellow" :
                            stat.pitcher_matchup_rating_thirty_percent >= 6.4 ? "lightpink" : "#d0d0d0"

                        const ratingColor =
                            stat.pitcher_matchup_rating_thirty_percent <= 5.63 ? "#0c447c" :
                            stat.pitcher_matchup_rating_thirty_percent > 5.63 && stat.pitcher_matchup_rating_thirty_percent <= 5.85 ? "#1a5c1a" :
                            stat.pitcher_matchup_rating_thirty_percent > 5.85 && stat.pitcher_matchup_rating_thirty_percent < 6.05 ? "#633806" :
                            stat.pitcher_matchup_rating_thirty_percent >= 6.4 ? "#791F1F" : "#2c2c2a"

                        const strikeoutRateBg =
                            stat.avg_hitter_matchup_k_pct >= 30.0 ? "lightblue" :
                            stat.avg_hitter_matchup_k_pct >= 26.0 && stat.avg_hitter_matchup_k_pct < 30.0 ? "lightgreen" :
                            stat.avg_hitter_matchup_k_pct > 23.0 && stat.avg_hitter_matchup_k_pct < 26.0 ? "lightyellow" :
                            stat.avg_hitter_matchup_k_pct <= 20.0 ? "lightpink" : "transparent"

                        const groundballRateBg =
                            stat.avg_hitter_matchup_gb_pct >= 50.0 ? "lightblue" :
                            stat.avg_hitter_matchup_gb_pct >= 46.0 && stat.avg_hitter_matchup_gb_pct < 50.0 ? "lightgreen" :
                            stat.avg_hitter_matchup_gb_pct > 43.0 && stat.avg_hitter_matchup_gb_pct < 46.0 ? "lightyellow" :
                            stat.avg_hitter_matchup_gb_pct <= 37.0 ? "lightpink" : "transparent"

                        const hardContactBg =
                            stat.avg_hitter_matchup_hard_contact <= 26.0 ? "lightblue" :
                            stat.avg_hitter_matchup_hard_contact >= 26.01 && stat.avg_hitter_matchup_hard_contact < 29.0 ? "lightgreen" :
                            stat.avg_hitter_matchup_hard_contact >= 29.01 && stat.avg_hitter_matchup_hard_contact < 32.0 ? "lightyellow" :
                            stat.avg_hitter_matchup_hard_contact >= 36.5 ? "lightpink" : "transparent"

                        const isoBg =
                            stat.avg_hitter_matchup_iso <= 0.129 ? "lightblue" :
                            stat.avg_hitter_matchup_iso >= 0.130 && stat.avg_hitter_matchup_iso < 0.140 ? "lightgreen" :
                            stat.avg_hitter_matchup_iso >= 0.140 && stat.avg_hitter_matchup_iso < 0.150 ? "lightyellow" :
                            stat.avg_hitter_matchup_iso >= 0.175 ? "lightpink" : "transparent"

                        const wobaBg =
                            stat.avg_hitter_matchup_woba <= 0.280 ? "lightblue" :
                            stat.avg_hitter_matchup_woba > 0.280 && stat.avg_hitter_matchup_woba < 0.300 ? "lightgreen" :
                            stat.avg_hitter_matchup_woba >= 0.300 && stat.avg_hitter_matchup_woba < 0.330 ? "lightyellow" :
                            stat.avg_hitter_matchup_woba >= 0.350 ? "lightpink" : "transparent"

                        return (
                            <ul key={stat.id} className="singleGameMobileList">
                                <li className="singleGameListRow" style={{ borderLeft: `4px solid ${tier.bar}` }}>

                                    {/* ── Top: pitcher info | rating ── */}
                                    <div className="singleGameTopRow">
                                        <div className="singleGameLeftContent">
                                            <div style={teamStyles} className="hitterNameAndTeam">
                                                <span className="hitterName">{stat.pitcher_name}</span>
                                                <span className="batHand">({stat.throw_hand})</span>
                                                <span className="hitterTeam">{stat.pitcher_team}</span>
                                            </div>
                                            <span className="pitcherDetails">vs {stat.hitter_team}</span>
                                        </div>
                                        <span className="matchupRating" style={{ backgroundColor: ratingBg, color: ratingColor }}>
                                            {stat.pitcher_matchup_rating_thirty_percent.toFixed(3)}
                                        </span>
                                    </div>

                                    {/* ── Divider ── */}
                                    <div className="singleGameCardDivider" />

                                    {/* ── Bottom: hitter quality counts | tier ── */}
                                    <div className="singleGameCardBottom">
                                        <div className="countPills">
                                            <span className="countPillElite">{stat?.num_elite_rated_hitters ?? 0} Elite</span>
                                            <span className="countPillPoor">{stat?.num_poor_rated_hitters ?? 0} Poor</span>
                                            <span className="countPillNeutral">{stat?.num_hitters_above_avg_k_pct ?? 0} High K%</span>
                                            <span className="countPillNeutral">{stat?.num_hitters_above_avg_gb_pct ?? 0} High GB%</span>
                                        </div>
                                        {tier.label && (
                                            <span className="tierBadge" style={{ backgroundColor: tier.bg, color: tier.color }}>
                                                {tier.label}
                                            </span>
                                        )}
                                    </div>

                                    {/* ── Expand toggle ── */}
                                    <span className={isOpen ? "arrow open" : "arrow"} onClick={() => expandHitterRow(stat.id)}>▼</span>

                                    {isOpen && (
                                        <div className="expandedContent">
                                            <div className="expandedSection">
                                                <div className="secLabel">Pitcher Stats</div>
                                                <div className="pitcherStatsGrid">
                                                    <div className="statCell"><div className="statValue">{stat?.ip}</div><div className="statLabel">IP</div></div>
                                                    <div className="statCell"><div className="matchupChip">{stat?.avg_hitter_matchup_rating?.toFixed(3)}</div><div className="statLabel">Avg Matchup Rtg</div></div>
                                                    <div className="statCell"><div className="matchupChip" style={{ background: wobaBg }}>{stat.avg_hitter_matchup_woba?.toFixed(3)}</div><div className="statLabel">Avg wOBA</div></div>
                                                    <div className="statCell"><div className="matchupChip" style={{ background: isoBg }}>{stat.avg_hitter_matchup_iso?.toFixed(3)}</div><div className="statLabel">Avg ISO</div></div>
                                                    <div className="statCell"><div className="matchupChip" style={{ background: hardContactBg }}>{stat?.avg_hitter_matchup_hard_contact}%</div><div className="statLabel">Avg Hard%</div></div>
                                                    <div className="statCell"><div className="matchupChip" style={{ background: strikeoutRateBg }}>{stat?.avg_hitter_matchup_k_pct.toFixed(1)}%</div><div className="statLabel">Avg K%</div></div>
                                                    <div className="statCell"><div className="matchupChip" style={{ background: groundballRateBg }}>{stat?.avg_hitter_matchup_gb_pct.toFixed(1)}%</div><div className="statLabel">Avg GB%</div></div>
                                                </div>
                                            </div>

                                            <button className="moreMatchupButton" onClick={() => navigateToPitcherPage(stat?.pitcher_name, stat?.pitcher_id)}>More on this matchup</button>
                                        </div>
                                    )}
                                </li>
                            </ul>
                        )
                    })}
                </div>
            )}

            <ColorLabels />

            <div className="singleGameListContainer">
                {relevantHitterState.map((stat) => {
                    const isOpen = openStatId === stat.id
                    const tier = getHitterTierInfo(stat.hit_matchup_rating)
                    const teamStyles = { color: state.teamColors[stat.hitter_team].primary || "transparent" }

                    const matchupRatingBg =
                        stat.hit_matchup_rating >= 0.450 ? "lightblue" :
                        stat.hit_matchup_rating >= 0.400 ? "lightgreen" :
                        stat.hit_matchup_rating > 0.333 ? "lightyellow" :
                        stat.hit_matchup_rating <= 0.230 ? "lightpink" : "#d0d0d0"

                    const matchupRatingColor =
                        stat.hit_matchup_rating >= 0.450 ? "#0c447c" :
                        stat.hit_matchup_rating >= 0.400 ? "#1a5c1a" :
                        stat.hit_matchup_rating > 0.333 ? "#633806" :
                        stat.hit_matchup_rating <= 0.230 ? "#791F1F" : "#2c2c2a"

                    const matchupWOBABg =
                        stat.hit_matchup_woba >= 0.400 ? "lightblue" :
                        stat.hit_matchup_woba < 0.400 && stat.hit_matchup_woba > 0.350 ? "lightgreen" :
                        stat.hit_matchup_woba >= 0.315 && stat.hit_matchup_woba < 0.350 ? "lightyellow" :
                        stat.hit_matchup_woba <= 0.300 ? "lightpink" : "transparent"

                    const matchupISOBg =
                        stat.hit_matchup_iso >= 0.275 ? "lightblue" :
                        stat.hit_matchup_iso < 0.275 && stat.hit_matchup_iso >= 0.200 ? "lightgreen" :
                        stat.hit_matchup_iso > 0.160 && stat.hit_matchup_iso < 0.199 ? "lightyellow" :
                        stat.hit_matchup_iso <= 0.150 ? "lightpink" : "transparent"

                    const matchupHCBg =
                        stat.hit_matchup_hard_contact >= 50.0 ? "lightblue" :
                        stat.hit_matchup_hard_contact < 50.0 && stat.hit_matchup_hard_contact >= 40.0 ? "lightgreen" :
                        stat.hit_matchup_hard_contact >= 33.0 && stat.hit_matchup_hard_contact < 40.0 ? "lightyellow" :
                        stat.hit_matchup_hard_contact <= 29.0 ? "lightpink" : "transparent"

                    const matchupFBBg =
                        stat.hit_matchup_fb_pct >= 50.0 ? "lightblue" :
                        stat.hit_matchup_fb_pct < 50.0 && stat.hit_matchup_fb_pct >= 40.0 ? "lightgreen" :
                        stat.hit_matchup_fb_pct > 37.0 && stat.hit_matchup_fb_pct < 40.0 ? "lightyellow" :
                        stat.hit_matchup_fb_pct <= 30.0 ? "lightpink" : "transparent"

                    const upColor = "rgba(0,100,0)"
                    const downColor = "rgba(139,0,0)"
                    const wOBAChangeColor = stat.hit_woba_percent_change > 0 ? upColor : downColor
                    const ISOChangeColor = stat.hit_iso_percent_change > 0 ? upColor : downColor
                    const HCChangeColor = stat.hit_hard_contact_percent_change > 0 ? upColor : downColor
                    const HRFBChangeColor = stat.hit_hr_per_fb_percent_change > 0 ? upColor : downColor
                    const FBChangeColor = stat.hit_fb_percent_change > 0 ? upColor : downColor

                    const arsenalStars = stat.pitch_overall_stars
                    const arsenalPct   = stat.pitch_overall_arsenal_pct
                    const hasArsenal   = arsenalStars != null || arsenalPct != null

                    const arsenalLeft = hasArsenal ? (
                        <div className="arsenalPills">
                            <span className="arsenalLabel">Pitch Arsenal</span>
                            <span className="arsenalStarPill">
                                {Array.from({ length: 5 }, (_, i) => (
                                    <span key={i} style={{ color: i < (arsenalStars ?? 0) ? "#f5a623" : "#ccc" }}>★</span>
                                ))}
                            </span>
                            {stat.pitch_overall_low_data ? (
                                <span
                                    className="arsenalNoPitchData"
                                    title={`Only ${stat.pitch_overall_data_coverage_pct?.toFixed(0)}% of arsenal has enough PA`}
                                >
                                    No Pitch Data
                                </span>
                            ) : (
                                <span className="arsenalPctPill">
                                    {arsenalPct != null ? `${Number(arsenalPct).toFixed(1)}%` : "—"}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="arsenalPills">
                            <span className="arsenalLabel">Pitch Arsenal</span>
                            <span className="arsenalPending">No Pitch Arsenal Report</span>
                        </div>
                    )

                    return (
                        <ul key={stat.id} className="singleGameMobileList">
                            <li className="singleGameListRow" style={{ borderLeft: `4px solid ${tier.bar}` }}>

                                {/* ── Top: hitter info | rating ── */}
                                <div className="singleGameTopRow">
                                    <div className="singleGameLeftContent">
                                        <div style={teamStyles} className="hitterNameAndTeam">
                                            <span className="hitterName">{stat.hitter_name}</span>
                                            <span className="batHand">({stat.bat_hand})</span>
                                            <span className="hitterTeam">{stat.hitter_team}</span>
                                        </div>
                                        <span className="pitcherDetails">vs {stat.pitcher_name} ({stat.throw_hand})</span>
                                    </div>
                                    <span className="matchupRating" style={{ backgroundColor: matchupRatingBg, color: matchupRatingColor }}>
                                        {stat.hit_matchup_rating?.toFixed(3)}
                                    </span>
                                </div>

                                {/* ── Divider ── */}
                                <div className="singleGameCardDivider" />

                                {/* ── Bottom: arsenal | tier ── */}
                                <div className="singleGameCardBottom">
                                    {arsenalLeft}
                                    {tier.label && (
                                        <span className="tierBadge" style={{ backgroundColor: tier.bg, color: tier.color }}>
                                            {tier.label}
                                        </span>
                                    )}
                                </div>

                                {/* ── Expand toggle ── */}
                                <span className={isOpen ? "arrow open" : "arrow"} onClick={() => expandHitterRow(stat.id)}>▼</span>

                                {isOpen && (
                                    <div className="expandedContent">

                                        {/* Hitter Stats — 4-col grid */}
                                        <div className="expandedSection">
                                            <div className="secLabel">Hitter Stats</div>
                                            <div className="hitterStatsGrid">
                                                <div className="statCell"><div className="statValue">{stat.hit_pa}</div><div className="statLabel">PA</div></div>
                                                <div className="statCell"><div className="statValue">{stat.hit_wrc_plus}</div><div className="statLabel">wRC+</div></div>
                                                <div className="statCell"><div className="statValue">{stat.hit_woba}</div><div className="statLabel">wOBA</div></div>
                                                <div className="statCell"><div className="statValue">{stat.hit_iso}</div><div className="statLabel">ISO</div></div>
                                                <div className="statCell"><div className="statValue">{stat.hit_hard_contact_pct}%</div><div className="statLabel">Hard Ct</div></div>
                                                <div className="statCell"><div className="statValue">{stat.hit_fb_pct}%</div><div className="statLabel">FB%</div></div>
                                                <div className="statCell"><div className="statValue">{stat.hit_hr_per_fb}%</div><div className="statLabel">HR/FB</div></div>
                                            </div>
                                        </div>

                                        {/* Matchup Stats — 5-col grid */}
                                        <div className="expandedSection">
                                            <div className="secLabel">Matchup Stats</div>
                                            <div className="matchupStatsGrid">
                                                <div className="statCell"><div className="matchupChip" style={{ background: matchupWOBABg }}>{stat.hit_matchup_woba?.toFixed(3)}</div><div className="statLabel">wOBA</div></div>
                                                <div className="statCell"><div className="matchupChip" style={{ background: matchupISOBg }}>{stat.hit_matchup_iso?.toFixed(3)}</div><div className="statLabel">ISO</div></div>
                                                <div className="statCell"><div className="matchupChip" style={{ background: matchupHCBg }}>{stat.hit_matchup_hard_contact?.toFixed(1)}%</div><div className="statLabel">Hard Ct</div></div>
                                                <div className="statCell"><div className="matchupChip" style={{ background: matchupFBBg }}>{stat.hit_matchup_fb_pct?.toFixed(1)}%</div><div className="statLabel">FB Rate</div></div>
                                                <div className="statCell"><div className="matchupChip">{stat.hit_matchup_hr_per_fb?.toFixed(1)}%</div><div className="statLabel">HR/FB</div></div>
                                            </div>

                                            {/* Pct changes — same 5-col alignment */}
                                            {isActiveSub && (
                                                <div className="pctChangeGrid">
                                                    <div className="statCell"><div className="pctValue" style={{ color: wOBAChangeColor }}>{stat.hit_woba_percent_change > 0 ? "+" : ""}{stat.hit_woba_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                                                    <div className="statCell"><div className="pctValue" style={{ color: ISOChangeColor }}>{stat.hit_iso_percent_change > 0 ? "+" : ""}{stat.hit_iso_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                                                    <div className="statCell"><div className="pctValue" style={{ color: HCChangeColor }}>{stat.hit_hard_contact_percent_change > 0 ? "+" : ""}{stat.hit_hard_contact_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                                                    <div className="statCell"><div className="pctValue" style={{ color: FBChangeColor }}>{stat.hit_fb_percent_change > 0 ? "+" : ""}{stat.hit_fb_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                                                    <div className="statCell"><div className="pctValue" style={{ color: HRFBChangeColor }}>{stat.hit_hr_per_fb_percent_change > 0 ? "+" : ""}{stat.hit_hr_per_fb_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                                                </div>
                                            )}
                                        </div>

                                        <button className="moreMatchupButton" onClick={() => navigateToHitterPage(stat?.hitter_name, stat?.hitter_id)}>More on this matchup</button>
                                    </div>
                                )}
                            </li>
                        </ul>
                    )
                })}
            </div>
        </div>
    )
}